import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Share } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button.tsx";
import { ClipboardField } from "@/components/Interface/ClipboardField/ClipboardField.tsx";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule.tsx";
import { SharePanel } from "@/components/Portal/FriendsWithBenefits/SharePanel.tsx";
import { useIsMobile } from "@/hooks/useIsMobile";

interface InviteFriendsCardProps {
  dataTestid?: string;
  invitationLink: string;
  /** A full group takes no new members, so inviting is switched off. */
  disabled?: boolean;
}

/**
 * The invitation link with a copy action, plus the share button. On desktop the
 * button opens the share-target panel; on mobile it hands off to the OS share
 * sheet (with a clipboard fallback).
 */
export const InviteFriendsCard = ({
  dataTestid,
  invitationLink,
  disabled,
}: InviteFriendsCardProps) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "friendsWithBenefits.groupOverview",
  });
  const isMobile = useIsMobile();
  // Without a link, there is nothing to copy or share yet.
  const isInvitingDisabled = disabled || !invitationLink;
  const [isSharePanelOpen, setIsSharePanelOpen] = useState(false);
  const sharePanelId = useId();
  const shareContainer = useRef<HTMLDivElement>(null);

  const shareText = `${t("shareSubHeadline").replace(/<br\s*\/?>/g, "\n")}\n\n${t("shareText")}\n`;

  const handleShare = async () => {
    if (isInvitingDisabled) {
      return;
    }

    if (isMobile) {
      if (navigator.share) {
        try {
          await navigator.share({
            title: t("shareTitle"),
            // Include the invitation link for targets that only receive `text`.
            text: `${shareText}\n${invitationLink}`,
          });
        } catch {
          // Cancelled or failed — fall back to copying the link only.
          await navigator.clipboard.writeText(invitationLink);
        }
      } else {
        await navigator.clipboard.writeText(invitationLink);
      }

      return;
    }

    setIsSharePanelOpen((isOpen) => !isOpen);
  };

  // A group that fills up while the panel is open must not keep live share
  // links around.
  useEffect(() => {
    if (isInvitingDisabled) {
      setIsSharePanelOpen(false);
    }
  }, [isInvitingDisabled]);

  useEffect(() => {
    if (!isSharePanelOpen) {
      return;
    }

    const closeOnOutsideInteraction = (event: MouseEvent) => {
      if (!shareContainer.current?.contains(event.target as Node)) {
        setIsSharePanelOpen(false);
      }
    };

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsSharePanelOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideInteraction);
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideInteraction);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isSharePanelOpen]);

  return (
    <CardModule
      dataTestid={dataTestid}
      title={t("inviteFriends")}
      variant="ivory"
      stickyHeader={false}
      actions={
        <div className="relative" ref={shareContainer}>
          <Button
            dataTestid="fwb-share-invitation"
            variant="secondary"
            ariaLabel={t("share")}
            icon={<Share />}
            disabled={isInvitingDisabled}
            aria-expanded={isMobile ? undefined : isSharePanelOpen}
            aria-controls={isMobile ? undefined : sharePanelId}
            onClick={handleShare}
          />
          {!isMobile && isSharePanelOpen && (
            <div
              id={sharePanelId}
              className="absolute right-0 top-full z-20 pt-2"
            >
              <SharePanel
                dataTestid="fwb-share-panel"
                invitationLink={invitationLink}
              />
            </div>
          )}
        </div>
      }
    >
      <ClipboardField
        dataTestid="fwb-invitation-link"
        content={invitationLink}
        successMessage={t("linkCopied")}
        errorMessage={t("linkCopyError")}
        disabled={isInvitingDisabled}
      />
    </CardModule>
  );
};
